import React, { useEffect, useState } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { Header, Footer, TopPromoBanner, Alert } from '@amzn/burberry-ui-components';
import { MbmProvider } from '@amzn/react-arb-tools';
import { LocalizationContextBuilder } from '@amzn/arb-tools';
import { DEFAULT_LOCALE, TRANSLATION_ROUTE } from '../constants';
import arbManifest from '../i18n/translations/arbManifest';
import { store, AppDispatch, storageKey } from 'src/store/store';
import { Paths } from 'src/paths/Paths';
import { UH_OH, NOT_FOUND_STRING, GENERIC_ERROR_STRING } from '@amzn/d2c-application-website-common';
import { getSite, getVersion, getIsPreview, fetchSiteConfig, generateCSSVariables } from 'src/utils/siteUtils';
import { getHeaderProps, getFooterProps, getTopPromoBannerProps } from 'src/utils/templateProps';
import { metricEmitter } from 'src/utils/metricEmitter';
import { getStage } from 'src/utils/roadieHelper';
import { getActiveEngagementId } from 'src/utils/engagement';
import { setTemplate, setManifest, setTemplateLoading, setTemplateError, selectTemplate, selectTemplateError, selectManifest } from 'src/store/templateSlice';
import { selectCart } from 'src/store/cartSlice';
import { selectExpiryTime, selectCsrfToken, logOut, selectValidateTokenComplete, setAuthInProgress } from 'src/store/authSlice';
import { fetchCollectionV2, replaceMerchV2State } from 'src/store/merchV2Slice';
import { setIdentifierState, selectSessionId, SIGN_OUT_SUCCESS, SIGN_OUT_ERROR } from '@amzn/d2c-application-website-common';
import { v4 as uuidv4 } from 'uuid';
import { setEngagementId } from 'src/services/engagementId';
import { awsRum } from 'src/rum';
import CloseWindow from './CloseWindow';

import Home from 'src/pages/Home/Home';
import ProductDetail from 'src/pages/ProductDetail/ProductDetail';
import Cart from 'src/pages/Cart/Cart';
import Order from 'src/pages/Order/Order';
import Success from 'src/pages/Success/Success';
import ErrorPage from 'src/pages/Error/Error';
import FAQ from 'src/pages/FAQ/FAQ';
import Terms from 'src/pages/Terms/Terms';
import PrivacyPolicy from 'src/pages/PrivacyPolicy/PrivacyPolicy';

const AppContent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const template = useSelector(selectTemplate);
  const templateError = useSelector(selectTemplateError);
  const manifest = useSelector(selectManifest);
  const cart = useSelector(selectCart);
  const expiryTime = useSelector(selectExpiryTime);
  const csrfToken = useSelector(selectCsrfToken);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');
  const [siteReady, setSiteReady] = useState(false);
  const stage = getStage();
  const isPreview = getIsPreview();
  const env = isPreview ? 'PREVIEW' : 'LIVE';
  const siteId = getSite(stage, env);
  const version = getVersion(stage, env, isPreview) || 'LIVE';
  const validateTokenComplete = useSelector(selectValidateTokenComplete);
  const isLoggedIn = !!validateTokenComplete && !!expiryTime && Date.now() < expiryTime;
  const sessionId = useSelector(selectSessionId);

  useEffect(() => {
    if (!sessionId) {
      dispatch(setIdentifierState({ sessionId: uuidv4() }));
    }
  }, [dispatch, sessionId]);

  const validCartQuantity = cart?.quantity ?? 0;

  // Fetch site template on mount.
  // - PREVIEW mode: calls Mulberry internal API to get draft template from DDB.
  // - LIVE mode: fetches promoted config from S3 via CloudFront.
  useEffect(() => {
    if (!siteId) {
      setSiteReady(true);
      return;
    }

    dispatch(setTemplateLoading(true));
    fetchSiteConfig(siteId, version, isPreview)
      .then(({ template: data, manifest }) => {
        dispatch(setTemplate(data));
        if (manifest) {
          dispatch(setManifest(manifest));
        }
        const activeEngagementId = getActiveEngagementId(data);
        if (activeEngagementId) {
          setEngagementId(activeEngagementId);
        }
        setSiteReady(true);
      })
      .catch((error) => {
        console.error('Failed to load site config:', error);
        awsRum?.recordEvent('TemplateError', { message: error.message, siteId });
        dispatch(setTemplateError(error.message));
        setSiteReady(true);
      });
  }, [dispatch, siteId, version]);

  useEffect(() => {
    if (!template || !siteId) return;

    const styleId = `site-config-${siteId}`;
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) existingStyle.remove();

    const styleElement = document.createElement('style');
    styleElement.id = styleId;
    styleElement.textContent = generateCSSVariables(siteId, template);
    document.head.appendChild(styleElement);

    document.documentElement.setAttribute('site-id', siteId);

    if (template.icon) {
      let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      favicon.href = template.icon;
    }

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [template, siteId]);

  useEffect(() => {
    if (location.pathname === Paths.home && template?.title) {
      document.title = template.title;
    }
  }, [location.pathname, template?.title]);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);


  if (!siteReady) {
    return null;
  }

  if (templateError) {
    return <ErrorPage heading={UH_OH} message={GENERIC_ERROR_STRING} pageName="SiteLoadError" />;
  }

  if (!template) {
    return <div className="app" data-testid="app" />;
  }

  const headerProps = getHeaderProps(template);
  const footerProps = getFooterProps(template, manifest);
  const topPromoBannerProps = getTopPromoBannerProps(template);

  const handleLogout = async () => {
    dispatch(setAuthInProgress(true));
    try {
      await dispatch(logOut({ stage: getStage(), csrfToken })).unwrap();
      setAlertMessage(SIGN_OUT_SUCCESS);
      setAlertSeverity('success');
    } catch {
      setAlertMessage(SIGN_OUT_ERROR);
      setAlertSeverity('error');
    } finally {
      localStorage.removeItem(storageKey('authState'));
      navigate(Paths.home);
      dispatch(setAuthInProgress(false));
      const engagementId = getActiveEngagementId(template);
      if (engagementId) {
        dispatch(replaceMerchV2State({ collectionsV2: {}, getCollectionV2InProgress: false, errorV2: false }));
        dispatch(fetchCollectionV2({ engagementId, stage: getStage(), csrfToken }));
      }
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 4000);
    }
  };

  return (
    <div className="app" data-testid="app">
      <div className="app__background" aria-hidden="true" />
      <TopPromoBanner {...topPromoBannerProps} metricEmitter={metricEmitter}/>
      <Header {...headerProps} cartItemCount={validCartQuantity} onCartClick={() => navigate('/cart')}
              onNavLinkClick={(url) => navigate(url)} isLoggedIn={isLoggedIn}
              onLogoutClick={handleLogout} metricEmitter={metricEmitter}/>
      <main className="app__content">
        <div className={`app__content-inner${(template).centerOrFullWidth === 'center' ? ' app__content-inner--center' : ''}`}>
        <Routes>
          <Route path={Paths.home} element={<Home />} />
          <Route path={Paths.details} element={<ProductDetail />} />
          <Route path={Paths.cart} element={<Cart />} />
          <Route path={Paths.previewCheckout} element={<Order />} />
          <Route path={Paths.successCheckout} element={<Success />} />
          <Route path={Paths.faq} element={<FAQ />} />
          <Route path={Paths.terms} element={<Terms />} />
          <Route path={Paths.privacyPolicy} element={<PrivacyPolicy />} />
          <Route path={Paths.invalid} element={<ErrorPage heading={UH_OH} message={NOT_FOUND_STRING} pageName="NotFound" />} />
        </Routes>
        </div>
      </main>
      <Footer {...footerProps} onFooterLinkClick={(url) => navigate(url)} metricEmitter={metricEmitter} />
      {showAlert && <Alert message={alertMessage} severity={alertSeverity} />}
    </div>
  );
};

export const App: React.FC = () => {
  if (window.location.pathname === Paths.closeWindow) {
    return <CloseWindow />;
  }

  const mbmOptions = {
    arbManifest,
    defaultLocalizationContext: new LocalizationContextBuilder()
      .withLocale(DEFAULT_LOCALE)
      .withDefaultLocale(DEFAULT_LOCALE)
      .build(),
    urlPrefix: TRANSLATION_ROUTE,
  };

  return (
    <Provider store={store}>
      <MbmProvider {...mbmOptions}>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </MbmProvider>
    </Provider>
  );
};

export default App;
